import React, { useState, useRef } from 'react';

// Components
import Stage from './Stage';
import Display from './Display';
import StartButton from './StartButton';
import InfoTooltip from './InfoTooltip';
import RotationToggle from './RotationToggle';
import PathToggle from './PathToggle';
import NextTetromino from './NextTetromino';
import GhostTetromino from './GhostTetromino';
import SettingsModal from './SettingsModal';
import TouchControls from './TouchControls';
import MenuScreen from './MenuScreen';
import ScreenTapControls from './ScreenTapControls';

// Custom Hooks
import { usePlayer } from '../hooks/usePlayer';
import { useStage } from '../hooks/useStage';
import { useGameStatus } from '../hooks/useGameStatus';
import { useInterval } from '../hooks/useInterval';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useWindowSize } from '../hooks/useWindowSize';
import { useTouchControls } from '../hooks/useTouchControls';
import { randomTetromino } from '../tetrominos';
import { logMessage } from '../utils/logger';

// Helper Functions
import { createStage, checkCollision } from '../gameHelpers';

const Tetris: React.FC = () => {
  const [dropTime, setDropTime] = useState<number | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [paused, setPaused] = useState(false);
  const [savedDropTime, setSavedDropTime] = useState<number | null>(null);
  const [clockwiseRotation, setClockwiseRotation] = useLocalStorage('tetris-clockwiseRotation', true);
  const [showPath, setShowPath] = useLocalStorage('tetris-showPath', true);
  const [startingLevel, setStartingLevel] = useLocalStorage('tetris-startingLevel', 1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [nextTetromino, setNextTetromino] = useState(randomTetromino().shape);
  const [gameStarted, setGameStarted] = useState(false);
  
  // Create a ref for the game area
  const stageContainerRef = useRef<HTMLDivElement>(null);
  
  // Get window size to detect mobile devices
  const { isMobile } = useWindowSize();

  const [player, updatePlayerPos, resetPlayer, playerRotate] = usePlayer(nextTetromino, setNextTetromino);
  const [stage, setStage, rowsCleared] = useStage(player, resetPlayer);
  const [score, setScore, rows, setRows, level, setLevel] = useGameStatus(rowsCleared);

  const movePlayer = (dir: number) => {
    if (!checkCollision(player, stage, { x: dir, y: 0 })) {
      updatePlayerPos({ x: dir, y: 0, collided: false });
    }
  };

  const pauseGame = () => {
    if (!gameOver) {
      if (paused) {
        // Resume game
        setPaused(false);
        setDropTime(savedDropTime);
      } else {
        // Pause game
        setPaused(true);
        setSavedDropTime(dropTime);
        setDropTime(null);
      }
    }
  };

  // Create a ref for the tetris wrapper
  const tetrisWrapperRef = useRef<HTMLDivElement>(null);

  const startGame = () => {
    // If game is paused, resume it
    if (paused) {
      pauseGame();
      return;
    }
    
    // Reset everything
    setStage(createStage());
    setDropTime(calculateDropTime(startingLevel));
    setSavedDropTime(calculateDropTime(startingLevel));
    setNextTetromino(randomTetromino().shape);
    resetPlayer();
    setGameOver(false);
    setPaused(false);
    setScore(0);
    setRows(0);
    setLevel(startingLevel);
    setGameStarted(true);
    
    // Focus the game area after a short delay to ensure the DOM has updated
    setTimeout(() => {
      if (tetrisWrapperRef.current) {
        tetrisWrapperRef.current.focus();
      }
    }, 10);
  };

  // Track last rotation time for grace period
  const lastRotationTimeRef = useRef<number>(0);
  const ROTATION_GRACE_PERIOD = 500; // 500ms grace period

  const drop = () => {
    // Increase level when player has cleared 10 rows
    if (rows > (level + 1) * 10) {
      setLevel(prev => prev + 1);
      // Also increase speed
      setDropTime(calculateDropTime(level + 1));
    }

    if (!checkCollision(player, stage, { x: 0, y: 1 })) {
      updatePlayerPos({ x: 0, y: 1, collided: false });
    } else {
      // Check if we're within the rotation grace period
      const currentTime = Date.now();
      const timeSinceLastRotation = currentTime - lastRotationTimeRef.current;
      
      if (timeSinceLastRotation < ROTATION_GRACE_PERIOD) {
        // We're in the grace period, don't mark as collided yet
        updatePlayerPos({ x: 0, y: 0, collided: false });
      } else {
        // Game Over
        if (player.pos.y < 1) {
          setGameOver(true);
          setDropTime(null);
        }
        updatePlayerPos({ x: 0, y: 0, collided: true });
      }
    }
  };

  const keyUp = ({ keyCode }: { keyCode: number }): void => {
    if (!gameOver) {
      // Activate the interval again when user releases down arrow
      if (keyCode === 40) {
        setDropTime(calculateDropTime(level));
      }
    }
  };
  
  // Calculate drop time based on level
  const calculateDropTime = (lvl: number) => {
    return 1000 / (lvl + 1) + 200;
  };

  const dropPlayer = () => {
    setDropTime(null);
    drop();
  };

  // Hard drop implementation with fix for collision issue
  const hardDrop = () => {
    // First find how far the piece can drop
    let dropDistance = 0;
    while (!checkCollision(player, stage, { x: 0, y: dropDistance + 1 })) {
      dropDistance++;
    }

    logMessage(`Hard drop: dropDistance = ${dropDistance}`);
    logMessage(`Hard drop: Player position before update: x=${player.pos.x}, y=${player.pos.y}`);
    
    // Move the piece to its final position
    if (dropDistance > 0) {
      updatePlayerPos({ x: 0, y: dropDistance, collided: true });
      
      // Add a small delay before resetting player to ensure stage updates properly
      setTimeout(() => {
        if (setStage) {
          setStage(prevStage => prevStage); // Force update
        }
      }, 10);
    }
  };


  const move = (event: React.KeyboardEvent): void => {
    const { keyCode } = event;
    if (!gameOver) {
      // Handle Escape key for pause
      if (keyCode === 27) { // ESC key
        pauseGame();
        return;
      }
      
      // Only process movement keys if game is not paused
      if (!paused) {
        if (keyCode === 37) {
          movePlayer(-1);
        } else if (keyCode === 39) {
          movePlayer(1);
        } else if (keyCode === 40) {
          dropPlayer(); // Down arrow moves objects faster
        } else if (keyCode === 38) {
          playerRotate(stage, clockwiseRotation ? 1 : -1);  // Up arrow rotates based on setting
          lastRotationTimeRef.current = Date.now(); // Update last rotation time
        } else if (keyCode === 32) {
          event.preventDefault(); // Prevent space from triggering buttons
          hardDrop();  // Space bar for instant drop
        }
      }
    }
  };

  // Use the original interval implementation
  useInterval(() => {
    drop();
  }, dropTime);

  // Set up touch controls
  useTouchControls({
    movePlayer,
    dropPlayer,
    hardDrop,
    playerRotate,
    stage,
    clockwiseRotation,
    paused,
    gameOver
  });

  return (
    <div className="tetris-wrapper" ref={tetrisWrapperRef} role="button" tabIndex={0} onKeyDown={gameStarted ? move : undefined} onKeyUp={gameStarted ? keyUp : undefined}>
      {!gameStarted ? (
        <MenuScreen 
          startGame={startGame} 
          openSettings={() => setSettingsOpen(true)} 
        />
      ) : (
        <div className="tetris">
          <div className="stage-container" ref={stageContainerRef}>
            <Stage stage={stage} />
            {showPath && !paused && !gameOver && (
              <GhostTetromino player={player} stage={stage} />
            )}
            {paused && (
              <div className="pause-overlay">
                <div className="pause-text">PAUSED</div>
                <div className="pause-subtext">{isMobile ? 'Tap to resume' : 'Press ESC to resume'}</div>
              </div>
            )}
            {isMobile && !paused && !gameOver && (
              <ScreenTapControls
                moveLeft={() => movePlayer(-1)}
                moveRight={() => movePlayer(1)}
                rotate={() => playerRotate(stage, clockwiseRotation ? 1 : -1)}
                gameArea={stageContainerRef}
              />
            )}
          </div>
          <aside>
            {gameOver ? (
              <>
                <Display gameOver={gameOver} text="Game Over" />
                <button className="start-button" onClick={() => setGameStarted(false)}>
                  Back to Menu
                </button>
              </>
            ) : (
              <>
                <div className="mobile-score-display">
                  <Display text={`Score: ${score}`} />
                  {isMobile ? null : (
                    <>
                      <Display text={`Rows: ${rows}`} />
                      <Display text={`Level: ${level}`} />
                    </>
                  )}
                </div>
                {!isMobile && <StartButton callback={startGame} paused={paused} />}
                <div className="mobile-controls-row">
                  {isMobile && !gameOver && !paused && (
                    <button className="hard-drop-button" onClick={hardDrop}>
                      ⬇ Hard Drop
                    </button>
                  )}
                  <NextTetromino tetromino={nextTetromino} />
                </div>
                <div className="game-controls">
                  <button className="settings-button" onClick={() => setSettingsOpen(true)}>
                    Settings ⚙️
                  </button>
                  {isMobile && (
                    <button className="settings-button" onClick={pauseGame}>
                      {paused ? 'Resume' : 'Pause'}
                    </button>
                  )}
                  <InfoTooltip />
                </div>
              </>
            )}
          </aside>
          
          {/* No drop controls at the bottom anymore */}
        </div>
      )}
      <SettingsModal 
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        clockwiseRotation={clockwiseRotation}
        setClockwiseRotation={setClockwiseRotation}
        showPath={showPath}
        setShowPath={setShowPath}
        startingLevel={startingLevel}
        setStartingLevel={setStartingLevel}
      />
    </div>
  );
};

export default Tetris;